import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { app } from 'store';
import { useTracker } from 'components/Tracker/TrackerProvider';
import {
  defaultGraphicFields,
  defaultBarcodeFields,
  getEditedFields,
} from 'pages/MyAccount/customProfiles/profileFormatter';
import {
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Theme,
  Tabs,
  Tab,
  Box,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { saveAs } from 'file-saver';
import { fetchOrganizations } from 'store/myAccount/requests';
import { useQuery } from '@redux-requests/react';
import { Organization } from 'types';
import { ApiFetchOrganizations } from 'store/myAccount/types';
import {
  CustomProfile,
  useCustomProfiles,
  useDeleteCustomProfile,
  useUpdateCustomProfile,
  useUploadCustomProfile,
  useCustomProfileById,
  useCopyCustomProfile,
  fetchCustomProfileById,
} from 'store/queries/organizations/useCustomProfiles';
import { makeStyles } from 'tss-react/mui';
import { GVTypography } from 'components/lib';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import GVSelect from 'components/lib/GVSelect/GVSelect';
import ProfileEditorModal from 'components/common/ProfileEditor/ProfileEditorModal';
import OrganizationCombobox from './OrganizationCombobox';
import styles from './CustomProfiles.module.css';
import {
  formatGraphicProfileToIni,
  formatBarcodeProfileToIni,
  parseIniToProfileFields,
  parseIniToBarcodeProfileFields,
  clampGraphicProfileFields,
  clampBarcodeProfileFields,
  sanitizeProfileString,
  validateGraphicProfileString,
  validateBarcodeProfileString,
  ProfileFields,
} from './profileFormatter';
import { BarcodeProfileFields } from 'components/common/ProfileEditor/types';
import { useUserStateStore } from 'zstore/userStateStore';
import { getOrganizationId, isCustomProfiler } from 'utils/auth';
import useIsInternalOrg from 'hooks/useIsInternalOrg';
import classNames from 'classnames';

const PROFILE_TYPE_OPTIONS = [
  { label: 'Graphic', value: 'graphic' },
  { label: 'Barcode', value: 'barcode' },
];

const useStyles = makeStyles()((theme: Theme) => {
  return {
    tableContainer: {
      marginTop: '15px',
      maxHeight: '450px',
      overflowY: 'auto',
      width: '100%',
      maxWidth: '750px',
    },
    table: {
      width: '100%',
    },
    headerCell: {
      fontWeight: 'bold',
    },
    actionsCell: {
      textAlign: 'right',
      whiteSpace: 'nowrap',
    },
    uploadFileButton: {
      marginTop: theme.spacing(1),
      padding: theme.spacing(1, 2),
    },
    scrollBar: {
      scrollbarGutter: 'stable',
      '&::-webkit-scrollbar': {
        width: '10px',
        height: '10px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#393B3F',
        boxShadow: '0 0 1px 1px #474747',
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: '10px',
        background: 'rgba(100, 102, 105, 0.6)',
        boxShadow: '0 0 1px 1px #393B3F',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '#4e4e4e',
      },
    },
    deleteDialog: {
      margin: theme.spacing(2),
    },
    deleteDialogTitle: {
      textAlign: 'left',
      color: 'white',
    },
    deleteDialogContent: {
      padding: theme.spacing(3),
      textAlign: 'center',
    },
  };
});

const TruncatedText = ({ text }: { text: string }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <Tooltip title={showTooltip ? text : ''} arrow>
      <span
        onMouseEnter={(e) => setShowTooltip(e.currentTarget.scrollWidth > e.currentTarget.clientWidth)}
        style={{
          display: 'block',
          maxWidth: '250px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </span>
    </Tooltip>
  );
};

const CustomProfiles = () => {
  const tracker = useTracker();
  const dispatch = useDispatch();
  const { user } = useUserStateStore();
  const userOrgId = getOrganizationId(user);
  const isInternalOrg = useIsInternalOrg();

  const [organizationId, setOrganizationId] = useState<string>('');
  const [profileName, setProfileName] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [profileType, setProfileType] = useState<'graphic' | 'barcode'>('graphic');
  const [activeTab, setActiveTab] = useState<'graphic' | 'barcode'>('graphic');
  const [profileSearch, setProfileSearch] = useState('');
  const [organizationInfo, setOrganizationInfo] = useState<Partial<Organization> | undefined>(undefined);
  // Portal targets inside dialogs so Select/Combobox options stay outside MUI aria-hidden siblings.
  const [uploadDialogPortalContainer, setUploadDialogPortalContainer] = useState<HTMLDivElement | null>(null);
  const [copyDialogPortalContainer, setCopyDialogPortalContainer] = useState<HTMLDivElement | null>(null);
  const fetchOrganizationsQuery = useQuery({ type: fetchOrganizations });
  const { data: organizationsData, loading: isLoadingOrganizations } = fetchOrganizationsQuery;
  const organizations = isInternalOrg ? organizationsData || [] : [];

  useEffect(() => {
    // custom_profilers in the internal org use the org-search dropdown to choose
    // any org, so we don't auto-load. Everyone else (including internal org
    // admins/tenant-admins) should always see their own org's profiles.
    if ((!isInternalOrg || !isCustomProfiler(user)) && userOrgId) {
      setOrganizationId(userOrgId);
      const fullOrg = organizationsData?.find((org: Organization) => org.id === userOrgId);
      setOrganizationInfo(fullOrg ?? { id: userOrgId });
    }
  }, [isInternalOrg, user, userOrgId, organizationsData]);
  const uploadCustomProfileMutation = useUploadCustomProfile();
  const deleteCustomProfileMutation = useDeleteCustomProfile();
  const updateCustomProfileMutation = useUpdateCustomProfile();
  const copyCustomProfileMutation = useCopyCustomProfile();
  const { data, isLoading } = useCustomProfiles(organizationId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isOrgIdValid, setIsOrgIdValid] = useState<boolean>(true);
  const { classes } = useStyles();
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [profileToDelete, setProfileToDelete] = useState<{ id: string; name: string; profileType: string } | null>(
    null,
  );
  const [openNewProfileModal, setOpenNewProfileModal] = useState(false);
  const [openProfileEditorModal, setOpenProfileEditorModal] = useState(false);
  const [openCopyModal, setOpenCopyModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [profileToEdit, setProfileToEdit] = useState<CustomProfile | null>(null);
  const [profileToCopy, setProfileToCopy] = useState<CustomProfile | null>(null);
  const [targetOrgIdForCopy, setTargetOrgIdForCopy] = useState('');
  const [copiedProfileName, setCopiedProfileName] = useState<string>('');
  const [profileIdToEdit, setProfileIdToEdit] = useState<string | undefined>(undefined);
  const { data: fullProfileData } = useCustomProfileById(profileIdToEdit);

  const organizationOptions = useMemo(
    () =>
      organizations.map((organization: ApiFetchOrganizations) => ({
        label: organization.name,
        value: organization.id,
      })),
    [organizations],
  );

  const handleOrganizationValueChange = (orgId: string) => {
    if (orgId) {
      const organization = organizations.find((item: ApiFetchOrganizations) => item.id === orgId) ?? null;
      setOrganizationId(orgId);
      setOrganizationInfo((organization as Partial<Organization>) ?? { id: orgId });
      setIsOrgIdValid(true);
    } else {
      setOrganizationId('');
      setOrganizationInfo(undefined);
      setIsOrgIdValid(false);
    }
  };

  const handleTargetOrgIdForCopyChange = (orgId: string) => {
    setTargetOrgIdForCopy(orgId);
  };

  const filteredProfiles = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.filter((profile: CustomProfile) => {
      const matchesTab = (profile.profileType || 'graphic') === activeTab;
      const searchTerm = profileSearch.toLowerCase();
      const matchesSearch =
        !profileSearch ||
        profile.customProfileName.toLowerCase().includes(searchTerm) ||
        profile.id.toLowerCase().includes(searchTerm);
      return matchesTab && matchesSearch;
    });
  }, [data, activeTab, profileSearch]);

  useEffect(() => {
    if (fullProfileData && profileIdToEdit) {
      // Verify the fetched data matches the requested profile to avoid race conditions
      if (fullProfileData.id === profileIdToEdit) {
        setProfileName(fullProfileData.customProfileName);
        setProfileToEdit(fullProfileData);
        setProfileType(fullProfileData.profileType || 'graphic');
        setIsEditing(true);
        setOpenProfileEditorModal(true);
        setProfileIdToEdit(undefined); // Clear the ID to prevent re-triggering
      }
    }
  }, [fullProfileData, profileIdToEdit]);

  const readFileAsString = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    const isValidFile =
      file &&
      ((profileType === 'graphic' && file.name.endsWith('.ini')) ||
        (profileType === 'barcode' && file.name.endsWith('.xml')));

    if (isValidFile) {
      setSelectedFile(file);
    } else {
      const expectedFormat = profileType === 'graphic' ? '.ini' : '.xml';
      dispatch(app.actions.setSnackMessage({ message: `Please upload a valid ${expectedFormat} file`, type: 'error' }));
      setSelectedFile(null);
    }
  };

  const handleOpenNewProfileModal = () => {
    setProfileType(activeTab);
    setProfileName('');
    setOpenNewProfileModal(true);
  };

  const handleOpenCreateProfileModal = () => {
    setProfileType(activeTab);
    setProfileName('');
    setProfileToEdit(null);
    setIsEditing(false);
    setOpenProfileEditorModal(true);
  };
  const handleCloseNewProfileModal = () => {
    setOpenNewProfileModal(false);
    setProfileName('');
    setSelectedFile(null);
    setProfileToEdit(null);
    setIsEditing(false);
    setProfileType('graphic');
  };

  const normalizeProfileString = (rawString: string, type: 'graphic' | 'barcode'): string =>
    type === 'graphic'
      ? formatGraphicProfileToIni(clampGraphicProfileFields(parseIniToProfileFields(rawString)))
      : formatBarcodeProfileToIni(clampBarcodeProfileFields(parseIniToBarcodeProfileFields(rawString)));

  const handleConfirmUpload = async () => {
    if (selectedFile) {
      const rawProfileString = sanitizeProfileString(await readFileAsString(selectedFile));
      const validationError =
        profileType === 'graphic'
          ? validateGraphicProfileString(rawProfileString)
          : validateBarcodeProfileString(rawProfileString);
      if (validationError) {
        dispatch(app.actions.setSnackMessage({ message: validationError, type: 'error' }));
        return;
      }
      const profileString = normalizeProfileString(rawProfileString, profileType);

      uploadCustomProfileMutation.mutate(
        {
          customProfileString: profileString,
          customProfileName: profileName,
          orgId: organizationId,
          fileName: selectedFile.name,
          profileType: profileType,
        },
        {
          onSuccess: (result) => {
            const uploadedFields =
              profileType === 'graphic'
                ? parseIniToProfileFields(profileString)
                : parseIniToBarcodeProfileFields(profileString);
            const defaults = profileType === 'graphic' ? defaultGraphicFields : defaultBarcodeFields;
            tracker.track({
              name: 'custom-profile-created',
              data: {
                profileId: result.id,
                profileName,
                profileType,
                fieldsEdited: getEditedFields(uploadedFields, defaults),
              },
            });
            setProfileName('');
            handleCloseNewProfileModal();
            setSelectedFile(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          },
          onError: (error) => {
            console.error('Error uploading profile:', error);
          },
        },
      );
    }
  };

  const handleDeleteClick = (profile: CustomProfile) => {
    setProfileToDelete({
      id: profile.id,
      name: profile.customProfileName,
      profileType: profile.profileType ?? 'graphic',
    });
    setOpenDeleteModal(true);
  };

  const handleCopyClick = (profile: CustomProfile) => {
    setProfileToCopy(profile);
    setTargetOrgIdForCopy('');
    setCopiedProfileName(profile.customProfileName);
    setOpenCopyModal(true);
  };

  const handleDownloadClick = async (profile: CustomProfile) => {
    try {
      const fullProfile = await fetchCustomProfileById(profile.id);

      if (!fullProfile.customProfileString) {
        console.error('Profile not available');
        return;
      }

      // Determine file extension and MIME type based on profile type
      const profileType = fullProfile.profileType || profile.profileType || 'graphic';
      const fileExtension = profileType === 'graphic' ? 'ini' : 'xml';
      const mimeType = profileType === 'graphic' ? 'text/plain' : 'application/xml';

      // Use fileName if available, otherwise generate from customProfileName
      const fileName =
        fullProfile.fileName ||
        profile.fileName ||
        `${fullProfile.customProfileName || profile.customProfileName}.${fileExtension}`;

      // Create Blob and trigger download
      const blob = new Blob([fullProfile.customProfileString], { type: mimeType });
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error downloading profile:', error);
    }
  };

  const handleConfirmDelete = () => {
    if (profileToDelete) {
      deleteCustomProfileMutation.mutate(profileToDelete.id, {
        onSuccess: () => {
          tracker.track({
            name: 'custom-profile-deleted',
            data: {
              profileId: profileToDelete.id,
              profileName: profileToDelete.name,
              profileType: profileToDelete.profileType,
            },
          });
          setOpenDeleteModal(false);
          setProfileToDelete(null);
        },
        onError: (error) => {
          console.error('Error deleting profile:', error);
          setProfileToDelete(null);
        },
      });
    }
  };

  const handleEditClick = (profile: CustomProfile) => {
    setProfileIdToEdit(profile.id);
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleUpdateProfile = async () => {
    if (profileToEdit) {
      let profileString: string | undefined;
      if (selectedFile) {
        const rawProfileString = sanitizeProfileString(await readFileAsString(selectedFile));
        const validationError =
          profileType === 'graphic'
            ? validateGraphicProfileString(rawProfileString)
            : validateBarcodeProfileString(rawProfileString);
        if (validationError) {
          dispatch(app.actions.setSnackMessage({ message: validationError, type: 'error' }));
          return;
        }
        profileString = normalizeProfileString(rawProfileString, profileType);
      }
      updateCustomProfileMutation.mutate(
        {
          customProfileString: profileString,
          customProfileName: profileName,
          id: profileToEdit.id,
          orgId: organizationId,
          fileName: selectedFile ? selectedFile.name : profileToEdit.fileName,
          profileType: profileType,
        },
        {
          onSuccess: () => {
            console.log('Profile updated successfully');
            handleCloseNewProfileModal();
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          },
          onError: (error) => {
            console.error('Error updating profile:', error);
          },
        },
      );
    }
  };

  return (
    <>
      <div className={styles.root}>
        {isInternalOrg && isCustomProfiler(user) && (
          <>
            <Typography variant="h5" data-testid="organization-title">
              Organization
            </Typography>
            <OrganizationCombobox
              options={organizationOptions}
              value={organizationId}
              onValueChange={handleOrganizationValueChange}
              isLoading={isLoadingOrganizations}
              label="Search Organization"
              data-testid="organization-search-input"
            />
          </>
        )}

        {organizationId && !isLoading && organizationInfo && (
          <>
            <Typography variant="h5" style={{ marginTop: '20px' }}>
              Custom Profiles
            </Typography>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', marginTop: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => {
                  setActiveTab(newValue);
                  setProfileSearch('');
                }}
              >
                <Tab label="Graphic Profiles" value="graphic" />
                <Tab label="Barcode Profiles" value="barcode" />
              </Tabs>
            </Box>
            {data && Array.isArray(data) && (
              <>
                <GVTextField
                  className={styles.search}
                  label="Search profiles"
                  value={profileSearch}
                  onValueChange={(value) => setProfileSearch(value ?? '')}
                  aria-label="Search profiles by name or ID"
                />
                <TableContainer component={Paper} className={`${classes.tableContainer} ${classes.scrollBar}`}>
                  <Table size="small" stickyHeader className={classes.table}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.headerCell}>Profile ID</TableCell>
                        <TableCell className={classes.headerCell}>Profile Name</TableCell>
                        <TableCell className={classes.actionsCell}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody data-testid="custom-profile-table">
                      {filteredProfiles.map((profile: CustomProfile) => (
                        <TableRow key={profile.id} hover>
                          <TableCell>{profile.id}</TableCell>
                          <TableCell data-testid={`profile-name-${profile.customProfileName}`}>
                            <TruncatedText text={profile.customProfileName} />
                          </TableCell>
                          <TableCell className={classes.actionsCell}>
                            <IconButton
                              color="primary"
                              size="small"
                              data-testid={`profile-edit-${profile.customProfileName}`}
                              onClick={() => handleEditClick(profile)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            {isInternalOrg && isCustomProfiler(user) && (
                              <IconButton
                                color="primary"
                                size="small"
                                data-testid={`profile-copy-${profile.customProfileName}`}
                                onClick={() => handleCopyClick(profile)}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            )}
                            <IconButton
                              color="primary"
                              size="small"
                              data-testid={`profile-download-${profile.customProfileName}`}
                              onClick={() => handleDownloadClick(profile)}
                            >
                              <FileDownloadOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              color="secondary"
                              size="small"
                              data-testid={`profile-delete-${profile.customProfileName}`}
                              onClick={() => handleDeleteClick(profile)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            <Box sx={{ display: 'flex', gap: 2, marginTop: '20px' }}>
              <Button
                variant="contained"
                color="secondary"
                disabled={!organizationId || !isOrgIdValid || !organizationInfo}
                onClick={handleOpenNewProfileModal}
              >
                Upload {activeTab === 'graphic' ? 'Graphic' : 'Barcode'} Profile
              </Button>
              <Button
                variant="contained"
                color="secondary"
                disabled={!organizationId || !isOrgIdValid || !organizationInfo}
                onClick={handleOpenCreateProfileModal}
              >
                Create {activeTab === 'graphic' ? 'Graphic' : 'Barcode'} Profile
              </Button>
            </Box>
          </>
        )}
      </div>

      <Dialog open={openNewProfileModal} onClose={handleCloseNewProfileModal} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Profile' : 'Create New Profile'}</DialogTitle>
        <DialogContent
          ref={setUploadDialogPortalContainer}
          style={{ padding: '20px', height: '440px', overflowY: 'auto', marginTop: '-30px' }}
          className={classNames(classes.scrollBar, styles.dialog)}
        >
          <GVTextField
            label={<span className={styles.label}>Profile Name</span>}
            data-testid="profile-name-input"
            value={profileName}
            onValueChange={(value) => setProfileName(value ?? '')}
          />
          <GVSelect
            id="profile-type-select"
            label={<span className={styles.label}>Profile Type</span>}
            value={profileType}
            onValueChange={(value) => {
              setProfileType(value as 'graphic' | 'barcode');
              // Clear selected file when changing profile type to prevent uploading wrong file type
              setSelectedFile(null);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}
            options={PROFILE_TYPE_OPTIONS}
            container={uploadDialogPortalContainer}
          />
          <Typography variant="subtitle1" marginTop="20px">
            Upload a GVD custom profile file ({profileType === 'graphic' ? '.ini' : '.xml'}) for a specific organization
          </Typography>
          <div>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleFileClick}
              className={classes.uploadFileButton}
            >
              Upload File
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              id="profile-input"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              accept={profileType === 'graphic' ? '.ini' : '.xml'}
            />
            <GVTypography data-testid="profile-file-name">
              {selectedFile ? selectedFile.name : profileToEdit?.fileName || ''}
            </GVTypography>
          </div>
          {isInternalOrg && profileName && (selectedFile || profileToEdit?.fileName) && (
            <div style={{ marginTop: '16px' }}>
              <DialogContentText>
                {isEditing ? 'You are updating' : 'You are uploading'} the profile '{profileName}'{' '}
                {isEditing ? 'from' : 'to'} this organization:
              </DialogContentText>
              <List>
                <ListItem>
                  <ListItemText primary="Display Name:" secondary={organizationInfo?.name} data-testid="org-display" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Organization Id:" secondary={organizationInfo?.id} data-testid="org-id" />
                </ListItem>
              </List>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewProfileModal} color="primary">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={
              (!selectedFile && !profileToEdit?.fileName) ||
              !organizationId ||
              !profileName ||
              !isOrgIdValid ||
              !organizationInfo
            }
            onClick={() => (isEditing && profileToEdit ? handleUpdateProfile() : handleConfirmUpload())}
          >
            {isEditing ? 'Update' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        classes={{ paper: classes.deleteDialog }}
      >
        <DialogTitle className={classes.deleteDialogTitle}>Confirm Deletion</DialogTitle>
        <DialogContent className={classes.deleteDialogContent}>
          <DialogContentText>
            Are you sure you want to delete the custom profile '{profileToDelete?.name}'?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)} variant="contained" color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="secondary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCopyModal}
        onClose={() => {
          setOpenCopyModal(false);
          setProfileToCopy(null);
          setTargetOrgIdForCopy('');
          setCopiedProfileName('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Copy Profile to Organization</DialogTitle>
        <DialogContent ref={setCopyDialogPortalContainer} style={{ padding: '20px', minHeight: '250px' }}>
          <DialogContentText style={{ marginBottom: '20px' }}>
            Copy "{profileToCopy?.customProfileName}" to another organization:
          </DialogContentText>
          <GVTextField
            className={styles.copiedName}
            label="Profile Name"
            value={copiedProfileName}
            onValueChange={(value) => setCopiedProfileName(value ?? '')}
          />
          <OrganizationCombobox
            className={styles.combobox}
            options={organizationOptions}
            value={targetOrgIdForCopy}
            onValueChange={handleTargetOrgIdForCopyChange}
            isLoading={isLoadingOrganizations}
            label="Select Target Organization"
            container={copyDialogPortalContainer}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenCopyModal(false);
              setProfileToCopy(null);
              setTargetOrgIdForCopy('');
              setCopiedProfileName('');
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={!targetOrgIdForCopy || !profileToCopy || !copiedProfileName.trim()}
            onClick={() => {
              if (profileToCopy && targetOrgIdForCopy && copiedProfileName.trim()) {
                copyCustomProfileMutation.mutate(
                  {
                    profileId: profileToCopy.id,
                    targetOrgId: targetOrgIdForCopy,
                    newProfileName: copiedProfileName.trim(),
                  },
                  {
                    onSuccess: () => {
                      setOpenCopyModal(false);
                      setProfileToCopy(null);
                      setTargetOrgIdForCopy('');
                      setCopiedProfileName('');
                    },
                  },
                );
              }
            }}
          >
            Copy
          </Button>
        </DialogActions>
      </Dialog>

      <ProfileEditorModal
        open={openProfileEditorModal}
        onClose={() => {
          setOpenProfileEditorModal(false);
          setProfileToEdit(null);
          setIsEditing(false);
          setProfileName('');
        }}
        onSave={(profileName: string, fields: ProfileFields | BarcodeProfileFields) => {
          let profileString: string;
          let rasterDPI: number | undefined;
          let barcodeReRasterDpi: number | undefined;

          if (profileType === 'graphic') {
            profileString = formatGraphicProfileToIni(fields as ProfileFields);
          } else {
            const barcodeFields = fields as BarcodeProfileFields;
            profileString = formatBarcodeProfileToIni(barcodeFields);
            // Extract DPI fields (not part of XML profile string)
            rasterDPI = barcodeFields.rasterDPI ? Number(barcodeFields.rasterDPI) : undefined;
            barcodeReRasterDpi = barcodeFields.barcodeReRasterDpi
              ? Number(barcodeFields.barcodeReRasterDpi)
              : undefined;
          }

          if (profileToEdit) {
            // Editing existing profile
            updateCustomProfileMutation.mutate(
              {
                customProfileString: profileString,
                customProfileName: profileName,
                id: profileToEdit.id,
                orgId: organizationId,
                fileName: profileToEdit.fileName,
                profileType: profileType,
                rasterDPI,
                barcodeReRasterDpi,
              },
              {
                onSuccess: () => {
                  const originalFields =
                    profileType === 'graphic'
                      ? parseIniToProfileFields(profileToEdit.customProfileString ?? '')
                      : {
                          ...parseIniToBarcodeProfileFields(profileToEdit.customProfileString ?? ''),
                          rasterDPI: profileToEdit.rasterDPI ? String(profileToEdit.rasterDPI) : undefined,
                          barcodeReRasterDpi: profileToEdit.barcodeReRasterDpi
                            ? String(profileToEdit.barcodeReRasterDpi)
                            : undefined,
                        };
                  tracker.track({
                    name: 'custom-profile-edited',
                    data: {
                      profileId: profileToEdit.id,
                      profileName,
                      profileType,
                      fieldsEdited: getEditedFields(fields, originalFields),
                    },
                  });
                  setOpenProfileEditorModal(false);
                  setProfileToEdit(null);
                  setIsEditing(false);
                  setProfileName('');
                },
                onError: (error) => {
                  console.error('Error updating profile:', error);
                },
              },
            );
          } else {
            // Creating new profile
            const fileExtension = profileType === 'graphic' ? '.ini' : '.xml';
            uploadCustomProfileMutation.mutate(
              {
                customProfileString: profileString,
                customProfileName: profileName,
                orgId: organizationId,
                fileName: `${profileName}${fileExtension}`,
                profileType: profileType,
                rasterDPI,
                barcodeReRasterDpi,
              },
              {
                onSuccess: (result) => {
                  const defaults = profileType === 'graphic' ? defaultGraphicFields : defaultBarcodeFields;
                  tracker.track({
                    name: 'custom-profile-created',
                    data: {
                      profileId: result.id,
                      profileName,
                      profileType,
                      fieldsEdited: getEditedFields(fields, defaults),
                    },
                  });
                  setOpenProfileEditorModal(false);
                  setProfileName('');
                },
                onError: (error) => {
                  console.error('Error creating profile:', error);
                },
              },
            );
          }
        }}
        profile={profileToEdit}
        profileType={profileType}
        initialProfileName={profileName}
      />
    </>
  );
};

export default CustomProfiles;
